import { z } from "zod";
import {
  dateFilterSchema,
  optionalIngestedDateFilterSchema
} from "../agents/customerInteractionFilters.js";
import { withErrorToolOutput } from "../agents/errorToolOutput.js";
import { assertUnreachable } from "../helpers/utilities.js";
import {
  automationDeliveryProviderSchema
} from "./automation.js";
import {
  automationReportInstanceReferenceSchema
} from "./automationReportRouting.js";
import { teammateIdSchema } from "./teammate.js";
const REPORT_PLAN_TOOL_ID = "plan_reports";
const REPORT_DELEGATE_TOOL_ID = "generate_report";
const REPORT_NOTIFY_TOOL_ID = "send_notifications";
const reportKeySchema = z.string().brand();
const SHARED_REPORT_KEY = reportKeySchema.parse("run-report");
const ROLLUP_KEY_PREFIX = "rollup:";
const SHARED_ROLLUP_REPORT_KEY = reportKeySchema.parse(
  `${ROLLUP_KEY_PREFIX}${SHARED_REPORT_KEY}`
);
function buildInstanceReportKey(type, id) {
  return reportKeySchema.parse(`${type}:${id}`);
}
function encodeInstanceReportKey(instance) {
  switch (instance.type) {
    case "teammate":
      return buildInstanceReportKey(instance.type, instance.teammate_id);
    case "manager":
      return buildInstanceReportKey(instance.type, instance.manager_id);
    case "group":
      return buildInstanceReportKey(instance.type, instance.group_id);
    default:
      return assertUnreachable(instance);
  }
}
function checkIsRollupReportKey(reportKey) {
  return reportKey.startsWith(ROLLUP_KEY_PREFIX);
}
function encodeRollupReportKey(instance) {
  return reportKeySchema.parse(
    `${ROLLUP_KEY_PREFIX}${encodeInstanceReportKey(instance)}`
  );
}
function encodeReportKey(source, instance) {
  switch (source) {
    case "calls":
      return instance ? encodeInstanceReportKey(instance) : SHARED_REPORT_KEY;
    case "rollup":
      return instance ? encodeRollupReportKey(instance) : SHARED_ROLLUP_REPORT_KEY;
    default:
      return assertUnreachable(source);
  }
}
function parseInstanceReference(candidate) {
  const reference = automationReportInstanceReferenceSchema.safeParse(candidate);
  return reference.success ? reference.data : null;
}
function decodeInstanceReference(type, id) {
  switch (type) {
    case "teammate":
      return parseInstanceReference({ type, teammate_id: id });
    case "manager":
      return parseInstanceReference({ type, manager_id: id });
    case "group":
      return parseInstanceReference({ type, group_id: id });
    default:
      return null;
  }
}
function decodeInstanceReportKey(value) {
  if (value === SHARED_REPORT_KEY || value === SHARED_ROLLUP_REPORT_KEY) {
    return null;
  }
  const source = value.startsWith(ROLLUP_KEY_PREFIX) ? "rollup" : "calls";
  const instanceKey = source === "rollup" ? value.slice(ROLLUP_KEY_PREFIX.length) : value;
  const separatorIndex = instanceKey.indexOf(":");
  if (separatorIndex === -1) {
    const managerId = teammateIdSchema.safeParse(instanceKey);
    return managerId.success && source === "calls" ? {
      key: reportKeySchema.parse(value),
      instance: { type: "manager", manager_id: managerId.data },
      source
    } : null;
  }
  const instance = decodeInstanceReference(
    instanceKey.slice(0, separatorIndex),
    instanceKey.slice(separatorIndex + 1)
  );
  return instance ? { key: reportKeySchema.parse(value), instance, source } : null;
}
const reportPlanFilterSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("instance"),
    instance: automationReportInstanceReferenceSchema
  }),
  z.object({
    type: z.literal("count"),
    max_reports: z.number().int().positive()
  })
]);
const reportPlanInputSchema = z.object({
  date: dateFilterSchema,
  ingested_date: optionalIngestedDateFilterSchema
});
const reportPlanOutputSchema = withErrorToolOutput(
  z.object({
    reports: z.array(
      z.object({
        report_key: z.string(),
        name: z.string()
      })
    ),
    /** Why the plan is empty when the run was narrowed to a report it lacks. */
    message: z.string().optional()
  })
);
const reportDelegateInputSchema = z.object({
  report_key: z.string()
});
const reportDelegateOutputSchema = withErrorToolOutput(
  z.object({
    status: z.enum(["saved", "not_planned", "not_ready"]),
    is_empty: z.boolean().optional(),
    message: z.string().optional()
  })
);
const notifyTargetOutcomeSchema = z.enum([
  "delivered",
  "failed",
  "skipped"
]);
const notifyRecipientReasonSchema = z.enum([
  "send_error",
  "user_not_found",
  "empty",
  "unresolved",
  "preview"
]);
const reportNotifyTargetSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("teammate"),
    teammate_id: teammateIdSchema,
    report_key: z.string(),
    email: z.string().optional(),
    outcome: notifyTargetOutcomeSchema,
    reason: notifyRecipientReasonSchema.optional()
  }),
  z.object({
    kind: z.literal("channel"),
    channel: z.object({ id: z.string(), name: z.string() }),
    report_key: z.string(),
    outcome: notifyTargetOutcomeSchema
  })
]);
const reportNotifyOutputObjectSchema = z.object({
  /**
   * The run-level retry signal the completion contract reads: "failed" only
   * when a *retryable* delivery failed (a send error or a channel-post error).
   * Any failed target makes the pass failed and spends the same notification
   * retry budget.
   */
  status: z.enum(["sent", "failed"]),
  /** The run's delivery provider, so the transcript stays self-describing. */
  delivery_provider: automationDeliveryProviderSchema,
  /** One row per (report, target): each person's copy, each channel post. */
  targets: z.array(reportNotifyTargetSchema)
});
const reportNotifyOutputSchema = withErrorToolOutput(
  reportNotifyOutputObjectSchema
);
function buildReportThreadId(automationRunId, reportKey) {
  return `${automationRunId}:${reportKey}`;
}
export {
  REPORT_DELEGATE_TOOL_ID,
  REPORT_NOTIFY_TOOL_ID,
  REPORT_PLAN_TOOL_ID,
  SHARED_REPORT_KEY,
  SHARED_ROLLUP_REPORT_KEY,
  buildReportThreadId,
  checkIsRollupReportKey,
  decodeInstanceReportKey,
  encodeInstanceReportKey,
  encodeReportKey,
  encodeRollupReportKey,
  notifyRecipientReasonSchema,
  notifyTargetOutcomeSchema,
  reportDelegateInputSchema,
  reportDelegateOutputSchema,
  reportKeySchema,
  reportNotifyOutputSchema,
  reportNotifyTargetSchema,
  reportPlanFilterSchema,
  reportPlanInputSchema,
  reportPlanOutputSchema
};
//# sourceMappingURL=reportOrchestration.js.map
